import axios from 'axios';
import { resetSessionTimer } from './sessionTimeout';
const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://127.0.0.1:3001';
// Auth API client
const authApi = axios.create({
    baseURL: `${API_BASE_URL}/api/auth`,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
});
// Add request interceptor to include auth token and reset session timer
authApi.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        // Reset session timer on any authenticated API call
        resetSessionTimer();
    }
    return config;
});
// Add response interceptor for error handling
authApi.interceptors.response.use((response) => response, (error) => {
    if (error.response?.status === 401) {
        // Token expired or invalid, clear storage
        clearAuth();
        window.location.href = '/dashboard/login';
    }
    return Promise.reject(error);
});
// Local storage helpers
const TOKEN_KEY = 'sharpshot_auth_token';
const USER_KEY = 'sharpshot_user';
export const getAuthToken = () => {
    return localStorage.getItem(TOKEN_KEY);
};
export const setAuthToken = (token) => {
    localStorage.setItem(TOKEN_KEY, token);
};
export const getCurrentUser = () => {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
};
export const setCurrentUser = (user) => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
};
export const clearAuth = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
};
export const isAuthenticated = () => {
    return getAuthToken() !== null && getCurrentUser() !== null;
};
// Auth API functions
export const login = async (credentials) => {
    try {
        const response = await authApi.post('/login', credentials);
        if (response.data.success) {
            console.log('Authentication successful, storing credentials');
            setAuthToken(response.data.data.token);
            setCurrentUser(response.data.data.user);
        }
        return response.data;
    }
    catch (error) {
        console.error('Login failed:', error.response?.data?.error || error.message);
        throw new Error(error.response?.data?.error || 'Login failed');
    }
};
export const register = async (userData) => {
    try {
        const response = await authApi.post('/register', userData);
        if (response.data.success) {
            setAuthToken(response.data.data.token);
            setCurrentUser(response.data.data.user);
        }
        return response.data;
    }
    catch (error) {
        throw new Error(error.response?.data?.error || 'Registration failed');
    }
};
export const logout = async () => {
    try {
        await authApi.post('/logout');
    }
    catch (error) {
        // Even if logout API fails, clear local storage
        console.error('Logout API error:', error);
    }
    finally {
        clearAuth();
    }
};
export const getCurrentUserProfile = async () => {
    try {
        const response = await authApi.get('/me');
        if (response.data.success) {
            setCurrentUser(response.data.data.user);
            return response.data.data.user;
        }
        throw new Error('Failed to get user profile');
    }
    catch (error) {
        throw new Error(error.response?.data?.error || 'Failed to get user profile');
    }
};
export const updateProfile = async (profileData) => {
    try {
        const response = await authApi.put('/profile', profileData);
        if (response.data.success) {
            setCurrentUser(response.data.data.user);
            return response.data.data.user;
        }
        throw new Error('Failed to update profile');
    }
    catch (error) {
        throw new Error(error.response?.data?.error || 'Failed to update profile');
    }
};
// Auth context helpers
export const initializeAuth = async () => {
    const token = getAuthToken();
    const user = getCurrentUser();
    if (!token || !user) {
        return null;
    }
    try {
        // Verify token is still valid by fetching current user
        const verifiedUser = await getCurrentUserProfile();
        console.log('Authentication restored for user:', verifiedUser.username);
        return verifiedUser;
    }
    catch (error) {
        console.log('Token validation failed, clearing stored credentials');
        // Token is invalid, clear auth
        clearAuth();
        return null;
    }
};
// Helper function to get auth headers for API calls
export const getAuthHeaders = () => {
    const token = getAuthToken();
    if (!token) {
        return {};
    }
    return {
        'Authorization': `Bearer ${token}`
    };
};
